import { LocalizationPipe } from '@abp/ng.core';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, input, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  createFormCanSaveSignal,
  dismissDirtyFormModal,
  handleBulkActionResultAndClose,
  isFieldInvalid,
  maxDecimalsValidator,
  preventScientificNotation,
  prepareBulkActionIds,
  ZendraLocalizationServices,
  ZnBulkActionResultService,
  ZnConfirmation,
} from '@zendra/shared';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IModalContent } from 'projects/shared/src/lib/zn-modal-wrapper/interfaces/IModalContent';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, finalize } from 'rxjs';
import { StandCycleRotationOrchestratorService } from 'orchestrator';
import { AssignEucalyptusPercentageInputDto } from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/stand-cycle-rotation/models';

@Component({
  selector: 'lib-action-assign-eucalyptus-percentage',
  imports: [LocalizationPipe, CommonModule, ReactiveFormsModule],
  templateUrl: './action-assign-eucalyptus-percentage.html',
})
export class ActionAssignEucalyptusPercentage implements IModalContent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly orchestratorService = inject(StandCycleRotationOrchestratorService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly bulkActionResultService = inject(ZnBulkActionResultService);

  selectedRecords = input<any[]>([]);
  selectedRecordIds = input<number[]>([]);

  saving = signal(false);

  localizationResource = ZendraLocalizationServices.LanguageService;
  entityResource = ZendraLocalizationServices.Geographic;
  readonly preventScientificNotation = preventScientificNotation;

  form: FormGroup = this.formBuilder.group({
    EucalyptusPercentage: [
      null,
      [Validators.required, Validators.min(0), Validators.max(1), maxDecimalsValidator(2)],
    ],
  });

  canSave = createFormCanSaveSignal(this.form);

  fieldInvalid(field: string): boolean {
    return isFieldInvalid(this.form, field);
  }

  save() {
    const ids = prepareBulkActionIds(this.form, this.saving(), this.selectedRecordIds());
    if (!ids) {
      return;
    }

    this.saving.set(true);
    const request: AssignEucalyptusPercentageInputDto = {
      ids,
      eucalyptusPercentage: Number(this.form.get('EucalyptusPercentage').value),
    };

    this.orchestratorService
      .assignEucalyptusPercentage(request)
      .pipe(
        catchError(error => {
          console.error(error);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.saving.set(false)),
      )
      .subscribe(response => {
        handleBulkActionResultAndClose(
          response,
          ids.length,
          this.bulkActionResultService,
          this.activeModal,
        );
      });
  }

  confirm(): void {
    this.save();
  }

  dismiss(): void {
    dismissDirtyFormModal(
      this.form,
      this.saving(),
      this.confirmationService,
      this.activeModal,
    );
  }
}
